import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import Shimmer from './Shimmer';
import './Horoscope.css';
import appConfig from '../config/appConfig.json';
import Nav from './Nav';
import Footer from './Footer';
import ReactGA from 'react-ga4';

const Horoscope = () => {
    const { sign, date, selectedOption } = useParams();
    const navigate = useNavigate();
    const [horoscope, setHoroscope] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentOption, setCurrentOption] = useState(selectedOption || 'daily');
    const [currentDate, setCurrentDate] = useState(date || new Date().toISOString().split('T')[0]);
    const [month, setMonth] = useState('');
    const [formattedDate, setFormattedDate] = useState('');

    useEffect(() => {
        const formatDate = (dateString) => {
            const dateObj = dateString ? new Date(dateString) : new Date();
            return dateObj.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
        };

        setFormattedDate(formatDate(currentDate));
    }, [currentDate]);

    useEffect(() => {
        const fetchHoroscope = async () => {
            if (!sign || !currentOption) return;

            setLoading(true);
            try {
                let endpoint, payload;

                if (currentOption === 'daily') {
                    endpoint = '/horoscope/get-daily-horoscope';
                    payload = { sign, date: currentDate };
                    
                    // First check cache
                    // const cacheResponse = await axios.post(
                    //     `${appConfig.REACT_APP_BACKEND_URL}/horoscope/check-cache`,
                    //     { sign, date: currentDate }
                    // );

                    // if (cacheResponse.data.exists && cacheResponse.data.horoscope) {
                    //     setHoroscope(cacheResponse.data.horoscope);
                    //     setMonth(cacheResponse.data.month);
                    //     setLoading(false);
                    //     return;
                    // }
                } else {
                    endpoint = `/horoscope/get-${currentOption}-horoscope`;
                    payload = { sign, date: currentDate };
                }

                const response = await axios.post(
                    `${appConfig.REACT_APP_BACKEND_URL}${endpoint}`,
                    payload,
                    { headers: { 'Content-Type': 'application/json' } }
                );

                setHoroscope(response.data.horoscope);
                if (currentOption === 'daily') {
                    setMonth(response.data.month || '');
                }

                // Track successful horoscope fetch
                ReactGA.event({
                    category: 'Horoscope',
                    action: `Fetched ${currentOption} horoscope`,
                    label: sign
                });
            } catch (error) {
                console.error(`Error fetching ${currentOption} horoscope:`, error);
                ReactGA.event({
                    category: 'Error',
                    action: `Failed to fetch ${currentOption} horoscope`,
                    label: error.message
                });
            } finally {
                setLoading(false);
            }
        };

        fetchHoroscope();
    }, [sign, currentOption, currentDate]);

    useEffect(() => {
        if (currentOption !== selectedOption || currentDate !== date) {
            navigate(`/horoscope/${sign}/${currentDate}/${currentOption}-horoscope`, { replace: true });
        }
    }, [currentOption, currentDate, navigate, sign, date, selectedOption]);

    const handleDateChange = (e) => {
        setCurrentDate(e.target.value);
    };

    const redirectChat = () => {
        ReactGA.event({
            category: 'Navigation',
            action: 'Clicked Chat Now',
            label: 'From Horoscope Page'
        });
        navigate('/chat');
    };

    if (loading) {
        return (
            <>
                <Nav />
                <div className="horoscope-container">
                    <Shimmer />
                </div>
                <Footer />
            </>
        );
    }

    if (!horoscope) {
        return (
            <>
                <Nav />
                <div className="horoscope-container">
                    <h1>Horoscope</h1>
                    <p className="error-message">No horoscope available for {sign}.</p>
                </div>
                <Footer />
            </>
        );
    }

    const renderHoroscopeContent = () => {
        const commonSections = (
            <>
                <div className="horoscope-section">
                    <h3>Summary (Saaransh)</h3>
                    <ReactMarkdown>{horoscope.summary}</ReactMarkdown>
                </div>
                <div className="horoscope-section">
                    <h3>Personal Life (Vyaktigat Jeevan)</h3>
                    <ReactMarkdown>{horoscope.personalLife}</ReactMarkdown>
                </div>
                <div className="horoscope-section">
                    <h3>Career (Vyavsaayik Jeevan)</h3>
                    <ReactMarkdown>{horoscope.career}</ReactMarkdown>
                </div>
                <div className="horoscope-section">
                    <h3>Health (Swasthya)</h3>
                    <ReactMarkdown>{horoscope.health}</ReactMarkdown>
                </div>
                <div className="horoscope-section">
                    <h3>Financial Condition (Aarthik Stithi)</h3>
                    <ReactMarkdown>{horoscope.finances}</ReactMarkdown>
                </div>
                <div className="horoscope-section">
                    <h3>Advice (Saavdhaniyan aur Salah)</h3>
                    <ReactMarkdown>{horoscope.advice}</ReactMarkdown>
                </div>
            </>
        );

        switch (currentOption) {
            case 'daily':
                return (
                    <>
                        <p className="date">
                            {formattedDate} {month && `(${month})`}
                        </p>
                        {commonSections}
                    </>
                );
            case 'weekly':
                return (
                    <>
                        <p className="date">Week: {horoscope.weekRange}</p>
                        {commonSections}
                    </>
                );
            case 'monthly':
                return (
                    <>
                        <p className="date">Month: {horoscope.month}</p>
                        {commonSections}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Nav />
            <div className="horoscope-container">
                <h1>Horoscope</h1>
                <h2 className="zodiac-sign">{sign.charAt(0).toUpperCase() + sign.slice(1)}</h2>
                
                {/* <div className="date-picker">
                    <input
                        type="date"
                        value={currentDate}
                        onChange={handleDateChange}
                        max={new Date().toISOString().split('T')[0]}
                        className="date-input"
                    />
                </div> */}

                <div className="toggle-buttons">
                    {['daily', 'weekly', 'monthly'].map((option) => (
                        <button
                            key={option}
                            className={currentOption === option ? 'active' : ''}
                            onClick={() => setCurrentOption(option)}
                        >
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                        </button>
                    ))}
                </div>

                <div className="horoscope-content">
                    {renderHoroscopeContent()}
                </div>
            </div>

            <div className="astrology-section">
                <div className="images">
                    <img src="/astro.png" alt="Astrologer" className="astro-img" />
                </div>
                <div className="content">
                    <h2>Why wait?</h2>
                    <p>
                        Get your free astrology reading with vedic rashi in just 30
                        seconds. Unlock your vedic birth chart and start your journey with our
                        astrologer for free.
                    </p>
                    <h3>Talk to astrologer now.</h3>
                    <button onClick={redirectChat} className="get-started-btn">
                        Chat now <span className="arrow">→</span>
                    </button>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default Horoscope;